<template>
  <template v-if="variant === variants.v1">
    <section
      class="layout-pt-lg layout-pb-lg tw-relative tw-overflow-hidden"
      :style="`background-color: var(--c-${vars.backgroundColor})`"
    >
      <BlockSymbol
        v-if="vars.watermarkImage"
        :block-logo-image="String(vars.watermarkImage)"
        :block-opacity="Number(vars?.watermarkOpacityNumber || 1)"
        position-text="left_bottom"
        :company-key="company.company_key"
      />
      <div
        class="container tw-flex tw-flex-col tw-items-center tw-gap-10 lg:tw-flex-row lg:tw-gap-20 xl:tw-gap-28"
        :class="[vars.isReversedBool ? 'lg:tw-flex-row' : 'lg:tw-flex-row-reverse']"
      >
        <div
          class="tw-relative tw-w-full"
          :style="
            vars.photoImage
              ? `border-bottom: 4px solid var(--c-primary); ${vars.isReversedBool ? 'border-right' : 'border-left'}: 4px solid var(--c-primary)`
              : ''
          "
        >
          <library-image
            v-if="vars.photoImage"
            :h-ratio="10"
            :w-ratio="16"
            :src="vars.photoImage ? String(vars.photoImage) : undefined"
            :style="{
              borderRadius: 'var(--rounded)',
              overflow: 'hidden',
            }"
          />
          <library-iframe v-if="!vars.photoImage && vars.videoUrl" :src="vars.videoUrl" :h-ratio="10" :w-ratio="16" />
        </div>
        <div class="tw-w-full">
          <h2 class="tw-mb-6 lg:tw-mb-12" :style="`color: var(--c-${vars.textColor})`">{{ vars.titleText }}</h2>
          <div class="tw-flex tw-flex-col">
            <div class="tw-flex tw-flex-col tw-gap-4">
              <p
                v-for="({ description }, index) in descriptions"
                :key="`contentbox-description-${index}`"
                :style="{ color: `var(--c-${vars.textColor})` }"
              >
                {{ description }}
              </p>
            </div>
            <div class="tw-mt-6 tw-flex tw-flex-wrap tw-justify-start tw-gap-3">
              <nuxt-link
                v-for="(button, index) in buttons"
                :key="`button-${index}`"
                :external="true"
                :class="[buttons.length >= 2 ? 'tw-w-full md:tw-w-auto' : 'tw-w-auto', `n-${button.button_type}`]"
                :to="button.button_link ? String(button.button_link) : ''"
              >
                {{ button.button_label }}
              </nuxt-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>

  <template v-else-if="variant === variants.v2">
    <section class="layout-pt-lg layout-pb-lg">
      <div class="container">
        <div class="row y-gap-30 justify-between align-items-center">
          <div class="col-lg-5">
            <div>
              <library-image
                :src="vars.photoImage ? String(vars.photoImage) : undefined"
                :w-ratio="1"
                :h-ratio="1.1"
                :alt="vars.titleText ? String(vars.titleText) : undefined"
              />
            </div>
          </div>

          <div class="col-lg-6">
            <div class="pt-50 md:pt-20">
              <h2 class="text-62 md:text-38 uppercase fw-500" :style="{ color: `var(--c-${vars.textColor})` }">
                {{ vars.titleText }}
              </h2>
              <h5 v-if="vars.subTitleText" class="tw-mt-8" :style="{ color: `var(--c-${vars.textColor})` }">
                {{ vars.subTitleText }}
              </h5>

              <p
                v-for="({ description }, index) in descriptions"
                :key="`contentbox-description-${index}`"
                class="text-18 tw-mt-8"
                :style="{ color: `var(--c-${vars.textColor})` }"
              >
                {{ description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>

  <template v-else-if="variant === variants.iphoneMockup">
    <section class="layout-pt-lg layout-pb-lg">
      <div class="container">
        <div
          class="row y-gap-30 justify-between align-items-center"
          :class="{ 'flex-row-reverse': vars.isReversedBool }"
        >
          <div class="col-lg-6">
            <common-iphone15-mockup
              :image="vars.photoImage ? String(vars.photoImage) : undefined"
              :video="vars.videoUrl ? String(vars.videoUrl) : undefined"
            />
          </div>

          <div class="col-lg-5">
            <h2 class="tw-mb-6 lg:tw-mb-12" :style="{ color: `var(--c-${vars.textColor})` }">{{ vars.titleText }}</h2>
            <div class="tw-flex tw-flex-col">
              <div class="tw-flex tw-flex-col tw-gap-4">
                <p
                  v-for="({ description }, index) in descriptions"
                  :key="`contentbox-description-${index}`"
                  :style="{ color: `var(--${vars.textColor})` }"
                >
                  {{ description }}
                </p>
              </div>
              <div v-if="buttons.length > 0" class="tw-mt-6 tw-flex tw-flex-wrap tw-justify-start tw-gap-3">
                <nuxt-link
                  v-for="(button, index) in buttons"
                  :key="`button-${index}`"
                  :external="true"
                  :class="[buttons.length >= 2 ? 'tw-w-full md:tw-w-auto' : 'tw-w-auto', `n-${button.type}`]"
                  :to="button.link ? String(button.link) : ''"
                >
                  {{ button.label }}
                </nuxt-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
import BlockSymbol from '~/components/common/BlockSymbol.vue';

export default defineNuxtComponent({
  name: 'ContentBoxTheme7',

  mixins: [ComponentMixin],

  components: {
    BlockSymbol,
  },

  data() {
    return {
      isVisible: false,
      variants: {
        v1: 'v1',
        v2: 'v2',
        iphoneMockup: 'iphoneMockup',
      },
    };
  },

  computed: {
    descriptions() {
      return this.groupedVariables.descriptions;
    },
    buttons() {
      return this.groupedVariables.buttons;
    },
  },
});
</script>
